import {
  HOME_SET_VIEW_ROLE,
  HOME_SET_WIDGET_PREFERENCES,
  HOME_UPDATE_WIDGET_PREFERENCE,
  HOME_RECORD_QUICK_LINK_CLICK,
  HOME_FETCH_POSTS_REQUEST,
  HOME_FETCH_POSTS_SUCCESS,
  HOME_FETCH_POSTS_FAILURE,
  HOME_FETCH_SPACES_REQUEST,
  HOME_FETCH_SPACES_SUCCESS,
  HOME_FETCH_SPACES_FAILURE,
  HOME_MARK_SPACE_VISITED_FROM_HOME,
  HOME_APPLY_PENDING_SPACE_REORDER,
  HOME_REFRESH_SPACES_CARD_DATA_REQUEST,
  HOME_REFRESH_SPACES_CARD_DATA_SUCCESS,
  HOME_REFRESH_SPACES_CARD_DATA_FAILURE,
  HOME_UPDATE_POST_COUNTS,
  HOME_PREPEND_NEW_POSTS,
  HOME_REMOVE_POST,
} from '@core/constants/actions';
import { HomeViewRole } from '@app/constants/home';
import {
  HomeSpaceApiItem,
  HomeSpaceEngagementApiItem,
  HomeSpaceProgressApiItem,
  HomeState,
  WidgetPreference,
} from '@core/types/state';
import {
  createInitialHomeSpacesByRoleState,
  isHomeViewRole,
  mergeHomeSpacesEngagementById,
  mergeHomeSpacesProgressById,
} from './homeSpacesRoleState';
import { moveHomeSpaceToFront } from './homeSpacesReorder';

const getInitialViewRole = (): HomeViewRole | null => {
  if (typeof window === 'undefined') return null;
  try {
    const saved = localStorage.getItem('homeViewRole');
    if (saved === 'educator' || saved === 'student') {
      return saved as HomeViewRole;
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
};

const initialState: HomeState = {
  viewRole: getInitialViewRole(),
  widgetPreferences: null,
  posts: [],
  postsLoading: false,
  postsHasMore: true,
  postsCursor: null,
  educatorPosts: false,
  spacesByRole: createInitialHomeSpacesByRoleState(),
  pendingSpaceReorder: null,
};

type HomeSetViewRoleAction = {
  type: typeof HOME_SET_VIEW_ROLE;
  viewRole: HomeViewRole;
};

type HomeSetWidgetPreferencesAction = {
  type: typeof HOME_SET_WIDGET_PREFERENCES;
  preferences: WidgetPreference[];
};

type HomeUpdateWidgetPreferenceAction = {
  type: typeof HOME_UPDATE_WIDGET_PREFERENCE;
  widgetId: string;
  isHidden: boolean;
};

type HomeRecordQuickLinkClickAction = {
  type: typeof HOME_RECORD_QUICK_LINK_CLICK;
  linkKey: string;
  role: string;
};

type HomeFetchPostsRequestAction = {
  type: typeof HOME_FETCH_POSTS_REQUEST;
  reset?: boolean;
  educatorPosts?: boolean;
};

type HomeFetchPostsSuccessAction = {
  type: typeof HOME_FETCH_POSTS_SUCCESS;
  posts: any[];
  cursor: string | null;
  hasMore: boolean;
  reset?: boolean;
};

type HomeFetchPostsFailureAction = {
  type: typeof HOME_FETCH_POSTS_FAILURE;
};

type HomeFetchSpacesRequestAction = {
  type: typeof HOME_FETCH_SPACES_REQUEST;
  role: HomeViewRole;
};

type HomeFetchSpacesSuccessAction = {
  type: typeof HOME_FETCH_SPACES_SUCCESS;
  role: HomeViewRole;
  spaces: HomeSpaceApiItem[];
  progress: HomeSpaceProgressApiItem[];
  engagement: HomeSpaceEngagementApiItem[];
  cursor: string | null;
  hasMore: boolean;
  /** Only these newly appended cards should show metrics skeletons. */
  loadingCardMetricsSpaceIds?: string[];
};

type HomeFetchSpacesFailureAction = {
  type: typeof HOME_FETCH_SPACES_FAILURE;
  role: HomeViewRole;
  errorType?: 'network' | 'api';
};

type HomeMarkSpaceVisitedFromHomeAction = {
  type: typeof HOME_MARK_SPACE_VISITED_FROM_HOME;
  spaceId: string;
  role: HomeViewRole;
};

type HomeApplyPendingSpaceReorderAction = {
  type: typeof HOME_APPLY_PENDING_SPACE_REORDER;
};

type HomeRefreshSpacesCardDataRequestAction = {
  type: typeof HOME_REFRESH_SPACES_CARD_DATA_REQUEST;
  role: HomeViewRole;
  spaceId: string;
};

type HomeRefreshSpacesCardDataSuccessAction = {
  type: typeof HOME_REFRESH_SPACES_CARD_DATA_SUCCESS;
  role: HomeViewRole;
  progress: HomeSpaceProgressApiItem[];
  engagement: HomeSpaceEngagementApiItem[];
};

type HomeRefreshSpacesCardDataFailureAction = {
  type: typeof HOME_REFRESH_SPACES_CARD_DATA_FAILURE;
  role: HomeViewRole;
  spaceId: string;
};

type HomeUpdatePostCountsAction = {
  type: typeof HOME_UPDATE_POST_COUNTS;
  postId: string;
  commentsCount: number;
  reactionsCount: number;
  postData?: Record<string, any>;
};

type HomePrependNewPostsAction = {
  type: typeof HOME_PREPEND_NEW_POSTS;
  posts: any[];
};

type HomeRemovePostAction = {
  type: typeof HOME_REMOVE_POST;
  postId: string;
};

type HomeAction =
  | HomeSetViewRoleAction
  | HomeSetWidgetPreferencesAction
  | HomeUpdateWidgetPreferenceAction
  | HomeRecordQuickLinkClickAction
  | HomeFetchPostsRequestAction
  | HomeFetchPostsSuccessAction
  | HomeFetchPostsFailureAction
  | HomeFetchSpacesRequestAction
  | HomeFetchSpacesSuccessAction
  | HomeFetchSpacesFailureAction
  | HomeMarkSpaceVisitedFromHomeAction
  | HomeApplyPendingSpaceReorderAction
  | HomeRefreshSpacesCardDataRequestAction
  | HomeRefreshSpacesCardDataSuccessAction
  | HomeRefreshSpacesCardDataFailureAction
  | HomeUpdatePostCountsAction
  | HomePrependNewPostsAction
  | HomeRemovePostAction;

const home = (
  state: HomeState = initialState,
  action: HomeAction
): HomeState => {
  switch (action.type) {
    case HOME_SET_VIEW_ROLE:
      return {
        ...state,
        viewRole: action.viewRole,
        posts: [],
        postsCursor: null,
        postsHasMore: true,
      };
    case HOME_SET_WIDGET_PREFERENCES:
      return {
        ...state,
        widgetPreferences: action.preferences,
      };
    case HOME_UPDATE_WIDGET_PREFERENCE:
      if (!state.widgetPreferences) return state;
      return {
        ...state,
        widgetPreferences: state.widgetPreferences.map((pref) =>
          pref.widgetId === action.widgetId
            ? {
                ...pref,
                isHidden: action.isHidden,
              }
            : pref
        ),
      };

    case HOME_RECORD_QUICK_LINK_CLICK: {
      if (!state.widgetPreferences) return state;
      return {
        ...state,
        widgetPreferences: state.widgetPreferences.map((pref) =>
          pref.widgetId === 'home-quick-links-widget'
            ? {
                ...pref,
                linkClicks: {
                  ...(pref as any).linkClicks,
                  [action.role]: {
                    ...((pref as any).linkClicks?.[action.role] || {}),
                    [action.linkKey]:
                      (((pref as any).linkClicks?.[action.role] || {})[
                        action.linkKey
                      ] || 0) + 1,
                  },
                },
              }
            : pref
        ),
      };
    }
    case HOME_FETCH_POSTS_REQUEST:
      return {
        ...state,
        postsLoading: true,
        posts: action.reset ? [] : state.posts,
        postsCursor: action.reset ? null : state.postsCursor,
        postsHasMore: action.reset ? true : state.postsHasMore,
        educatorPosts: action.reset
          ? !!action.educatorPosts
          : state.educatorPosts,
      };
    case HOME_FETCH_POSTS_SUCCESS:
      return {
        ...state,
        postsLoading: false,
        posts: action.reset ? action.posts : [...state.posts, ...action.posts],
        postsCursor: action.cursor,
        postsHasMore: action.hasMore,
      };
    case HOME_FETCH_POSTS_FAILURE:
      return {
        ...state,
        postsLoading: false,
      };
    case HOME_PREPEND_NEW_POSTS: {
      const existingIds = new Set(state.posts.map((p: any) => p.objectId));
      const uniqueNewPosts = action.posts.filter(
        (p: any) => !existingIds.has(p.objectId)
      );
      if (uniqueNewPosts.length === 0) return state;
      return {
        ...state,
        posts: [...uniqueNewPosts, ...state.posts],
      };
    }
    case HOME_FETCH_SPACES_REQUEST: {
      if (!isHomeViewRole(action.role)) {
        return state;
      }

      return {
        ...state,
        spacesByRole: {
          ...state.spacesByRole,
          [action.role]: {
            ...state.spacesByRole[action.role],
            loading: true,
            error: false,
            errorType: null,
          },
        },
      };
    }
    case HOME_FETCH_SPACES_SUCCESS: {
      if (!isHomeViewRole(action.role)) {
        return state;
      }

      const roleState = state.spacesByRole[action.role];
      const existingIds = new Set(
        roleState.spaces.map((space) => space.spaceId)
      );
      const newSpaces = action.spaces.filter(
        (space) => !existingIds.has(space.spaceId)
      );

      return {
        ...state,
        spacesByRole: {
          ...state.spacesByRole,
          [action.role]: {
            ...roleState,
            loading: false,
            // Only newly appended cards wait on metrics — keep older cards stable.
            loadingCardMetricsSpaceIds:
              (action.loadingCardMetricsSpaceIds?.length ?? 0) > 0
                ? newSpaces.map((space) => space.spaceId)
                : [],
            spaces: [...roleState.spaces, ...newSpaces],
            progressById: mergeHomeSpacesProgressById(
              roleState.progressById,
              action.progress
            ),
            engagementById: mergeHomeSpacesEngagementById(
              roleState.engagementById,
              action.engagement
            ),
            cursor: action.cursor,
            hasMore: action.hasMore,
          },
        },
      };
    }
    case HOME_FETCH_SPACES_FAILURE: {
      if (!isHomeViewRole(action.role)) {
        return state;
      }

      return {
        ...state,
        spacesByRole: {
          ...state.spacesByRole,
          [action.role]: {
            ...state.spacesByRole[action.role],
            loading: false,
            loadingCardMetricsSpaceIds: [],
            error: true,
            errorType: action.errorType ?? 'api',
          },
        },
      };
    }
    case HOME_MARK_SPACE_VISITED_FROM_HOME: {
      if (!isHomeViewRole(action.role)) {
        return state;
      }

      return {
        ...state,
        pendingSpaceReorder: {
          spaceId: action.spaceId,
          role: action.role,
        },
      };
    }
    case HOME_APPLY_PENDING_SPACE_REORDER: {
      const pending = state.pendingSpaceReorder;

      if (!pending || !isHomeViewRole(pending.role)) {
        return state;
      }

      const roleState = state.spacesByRole[pending.role];
      const reorderedSpaces = moveHomeSpaceToFront(
        roleState.spaces,
        pending.spaceId
      );

      if (!reorderedSpaces) {
        return {
          ...state,
          pendingSpaceReorder: null,
        };
      }

      return {
        ...state,
        pendingSpaceReorder: null,
        spacesByRole: {
          ...state.spacesByRole,
          [pending.role]: {
            ...roleState,
            spaces: reorderedSpaces,
          },
        },
      };
    }
    case HOME_REFRESH_SPACES_CARD_DATA_REQUEST: {
      if (!isHomeViewRole(action.role)) {
        return state;
      }

      return {
        ...state,
        spacesByRole: {
          ...state.spacesByRole,
          [action.role]: {
            ...state.spacesByRole[action.role],
            refreshingCardDataSpaceId: action.spaceId,
          },
        },
      };
    }
    case HOME_REFRESH_SPACES_CARD_DATA_SUCCESS: {
      if (!isHomeViewRole(action.role)) {
        return state;
      }

      const roleState = state.spacesByRole[action.role];

      return {
        ...state,
        spacesByRole: {
          ...state.spacesByRole,
          [action.role]: {
            ...roleState,
            refreshingCardDataSpaceId: null,
            loadingCardMetricsSpaceIds: [],
            progressById: mergeHomeSpacesProgressById(
              roleState.progressById,
              action.progress
            ),
            engagementById: mergeHomeSpacesEngagementById(
              roleState.engagementById,
              action.engagement
            ),
          },
        },
      };
    }
    case HOME_REFRESH_SPACES_CARD_DATA_FAILURE: {
      if (!isHomeViewRole(action.role)) {
        return state;
      }

      return {
        ...state,
        spacesByRole: {
          ...state.spacesByRole,
          [action.role]: {
            ...state.spacesByRole[action.role],
            refreshingCardDataSpaceId: null,
            loadingCardMetricsSpaceIds: [],
          },
        },
      };
    }
    case HOME_UPDATE_POST_COUNTS:
      return {
        ...state,
        posts: state.posts.map((post: any) => {
          const postId = post?.objectId || post?.id;
          if (postId === action.postId) {
            return {
              ...post,
              ...(action.postData || {}),
              _commentsCount: action.commentsCount,
              _reactionsCount: action.reactionsCount,
            };
          }
          return post;
        }),
      };
    case HOME_REMOVE_POST:
      return {
        ...state,
        posts: state.posts.filter((post: any) => {
          const id = post?.objectId || post?.id;
          return id !== action.postId;
        }),
      };
    default:
      return state;
  }
};

export default home;
